<template>
    <a-layout style="min-height: 100vh;">
        <UserHead />
        <a-layout>
            <a-layout-header style="padding: 0; background: #fff; border-bottom: 1px solid #f0f0f0;">
                <UserHeadTop />
            </a-layout-header>
            <a-layout-content class="pay-channel-content-div">
                <div class="pay-channel-card">
                    <div class="pay-channel-title">
                        <span>支付通道管理</span>
                        <a-button type="primary" @click="openAddDialog">新增通道</a-button>
                    </div>

                    <!-- 支付方式备注 -->
                    <div class="pay-mode-notice warning" v-if="orderPayMode === 'tenant'">
                        <div class="notice-icon">
                            <WarningOutlined />
                        </div>
                        <p>当前小店为<strong>自定义支付通道</strong>，游客访问小店后，将使用您自定义的支付通道进行支付，请确保余额充足，否则即使支付成功，余额支付开通授权时也会失败。
                        </p>
                    </div>
                    <div class="pay-mode-notice info" v-else-if="orderPayMode === 'system'">
                        <div class="notice-icon">
                            <InfoCircleOutlined />
                        </div>
                        <p>当前小店为<strong>系统支付通道</strong>，游客访问小店后，将使用系统支付通道进行支付，您自定义的支付通道不会生效，游客支付授权成功后，将发放反利给您。
                        </p>
                    </div>

                    <a-table :data-source="channelList" :columns="columns" :loading="loading" :pagination="false"
                        row-key="id" class="pay-channel-table" size="middle">
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'name'">
                                <span>{{ record.name || formatPlatform(record.platform) }}</span>
                            </template>
                            <template v-else-if="column.key === 'platform'">
                                <a-tag color="blue" size="small">{{ formatPlatform(record.platform) }}</a-tag>
                            </template>
                            <template v-else-if="column.key === 'type'">
                                <a-tag size="small">{{ formatType(record.type) }}</a-tag>
                            </template>
                            <template v-else-if="column.key === 'payMethod'">
                                <span>{{ formatPayMethod(record) }}</span>
                            </template>
                            <template v-else-if="column.key === 'config'">
                                <span>{{ configSummary(record.config) }}</span>
                            </template>
                            <template v-else-if="column.key === 'state'">
                                <a-tag :color="record.state === 1 ? 'success' : 'default'" size="small">
                                    {{ record.state === 1 ? '启用' : '禁用' }}
                                </a-tag>
                            </template>
                            <template v-else-if="column.key === 'action'">
                                <a-button size="small" @click="openEditDialog(record)">编辑</a-button>
                                <a-button size="small" danger @click="deleteChannel(record)">删除</a-button>
                            </template>
                        </template>
                    </a-table>
                </div>

                <!-- 新增/编辑弹窗 -->
                <a-modal v-model:open="dialogVisible" :title="isEdit ? '编辑支付通道' : '新增支付通道'" width="560px"
                    :mask-closable="false" :footer="null">
                    <a-form :model="channelForm" layout="vertical">
                        <a-form-item label="通道名称">
                            <a-input v-model:value="channelForm.name" placeholder="如：支付宝通道（留空则显示平台名）" />
                        </a-form-item>
                        <a-form-item label="支付平台" required>
                            <a-select v-model:value="editType" placeholder="选择支付平台" @change="onPlatformChange">
                                <a-select-option value="yipay">易支付</a-select-option>
                                <a-select-option value="alipay_dmf">支付宝当面付</a-select-option>
                                <a-select-option value="alipay_web">支付宝网站支付</a-select-option>
                            </a-select>
                        </a-form-item>
                        <!-- 易支付：选择支持的支付类型（可多选） -->
                        <a-form-item label="支付类型" required v-if="editType === 'yipay'">
                            <a-select v-model:value="selectedTypes" mode="multiple"
                                placeholder="选择支持的支付类型（可多选）">
                                <a-select-option value="wxpay">微信支付</a-select-option>
                                <a-select-option value="alipay">支付宝</a-select-option>
                                <a-select-option value="qqpay">QQ支付</a-select-option>
                                <a-select-option value="usdt">USDT</a-select-option>
                            </a-select>
                        </a-form-item>
                        <!-- 易支付配置 -->
                        <template v-if="editType === 'yipay'">
                            <a-form-item label="接口URL" required>
                                <a-input v-model:value="channelConfig.url" placeholder="https://pay.example.com" />
                            </a-form-item>
                            <a-form-item label="商户PID" required>
                                <a-input v-model:value="channelConfig.pid" placeholder="易支付商户ID" />
                            </a-form-item>
                            <a-form-item label="商户KEY" required>
                                <a-input-password v-model:value="channelConfig.key" placeholder="易支付商户密钥" />
                            </a-form-item>
                        </template>
                        <!-- 支付宝当面付配置 -->
                        <template v-else-if="editType === 'alipay_dmf'">
                            <a-form-item label="AppID" required>
                                <a-input v-model:value="channelConfig.app_id" placeholder="支付宝应用ID" />
                            </a-form-item>
                            <a-form-item label="公钥" required>
                                <a-textarea v-model:value="channelConfig.public_key" :rows="3"
                                    placeholder="支付宝公钥" />
                            </a-form-item>
                            <a-form-item label="私钥" required>
                                <a-textarea v-model:value="channelConfig.private_key" :rows="3"
                                    placeholder="应用私钥" />
                            </a-form-item>
                        </template>
                        <!-- 支付宝网站支付配置 -->
                        <template v-else-if="editType === 'alipay_web'">
                            <a-form-item label="AppID" required>
                                <a-input v-model:value="channelConfig.app_id" placeholder="支付宝应用ID" />
                            </a-form-item>
                            <a-form-item label="公钥" required>
                                <a-textarea v-model:value="channelConfig.public_key" :rows="3"
                                    placeholder="支付宝公钥" />
                            </a-form-item>
                            <a-form-item label="私钥" required>
                                <a-textarea v-model:value="channelConfig.private_key" :rows="3"
                                    placeholder="应用私钥" />
                            </a-form-item>
                        </template>
                        <a-form-item label="状态">
                            <a-switch v-model:checked="channelForm.state" :checked-value="1" :un-checked-value="2"
                                checked-children="启用" un-checked-children="禁用" />
                        </a-form-item>
                    </a-form>
                    <template #footer>
                        <a-button @click="dialogVisible = false">取消</a-button>
                        <a-button type="primary" @click="submitChannel" :loading="submitting">确认</a-button>
                    </template>
                </a-modal>
            </a-layout-content>
        </a-layout>
    </a-layout>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { message, Modal } from 'ant-design-vue'
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons-vue'
import UserHead from '@/components/UserHead.vue'
import UserHeadTop from '@/components/UserHeadTop.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL
const headers = { 'token': localStorage.getItem('token') || '' }

const channelList = ref<any[]>([])
const loading = ref(false)
const dialogVisible = ref(false)
const isEdit = ref(false)
const submitting = ref(false)
const orderPayMode = ref('')

const channelForm = ref<any>({
    id: 0,
    name: '',
    platform: 1,
    type: '',
    config: '',
    state: 1,
})

// 用于编辑/新增时的配置对象
const channelConfig = ref<Record<string, any>>({})

// 易支付时多选的支付类型
const selectedTypes = ref<string[]>([])

// 表格列定义
const columns = [
    { title: '通道名称', key: 'name', dataIndex: 'name' },
    { title: '支付平台', key: 'platform', width: 100 },
    { title: '支付类型', key: 'type', width: 130 },
    { title: '支付方式', key: 'payMethod', width: 130 },
    { title: '配置信息', key: 'config', ellipsis: true },
    { title: '状态', key: 'state', width: 80 },
    { title: '操作', key: 'action', width: 150, fixed: 'right' },
]

// platform映射：1=易支付，2=支付宝
const formatPlatform = (p: any) => {
    const map: Record<number, string> = { 1: '易支付', 2: '支付宝' }
    return map[p] || '未知'
}

// 解析type为数组（兼容数组、JSON字符串、旧格式如"['alipay']"）
const parseType = (t: any): string[] => {
    if (!t) return []
    if (Array.isArray(t)) return t
    try {
        const parsed = JSON.parse(t)
        if (Array.isArray(parsed)) return parsed
    } catch {
        // 旧格式如 "['alipay']" 不是合法JSON，尝试用正则提取
        const matches = String(t).match(/'([^']+)'/g)
        if (matches) return matches.map(m => m.replace(/'/g, ''))
    }
    return [String(t)]
}

// type映射（支持数组和字符串）
const formatType = (t: any) => {
    if (!t) return '-'
    const typeMap: Record<string, string> = { wxpay: '微信支付', alipay: '支付宝', qqpay: 'QQ支付', usdt: 'USDT' }
    const arr = parseType(t)
    if (arr.length === 0) return '-'
    return arr.map(item => typeMap[item] || item).join('、')
}

// 支付方式：根据platform和config.product_code判断
const formatPayMethod = (row: any) => {
    if (row.platform === 1) return '易支付(跳转)'
    if (row.platform === 2) {
        try {
            const config = typeof row.config === 'string' ? JSON.parse(row.config) : row.config
            if (config.product_code === 'FAST_INSTANT_TRADE_PAY') return '网站支付(跳转)'
            return '当面付(扫码)'
        } catch {
            return '当面付(扫码)'
        }
    }
    return '-'
}

// 编辑时实际使用的通道类型（仅UI使用）
const editType = ref('yipay')

// 根据row数据推断editType
const detectEditType = (row: any): string => {
    if (row.platform === 2) {
        const config = parseConfig(row.config)
        if (config.product_code === 'FAST_INSTANT_TRADE_PAY') return 'alipay_web'
        return 'alipay_dmf'
    }
    return 'yipay'
}

// 切换支付平台时清理无关配置
const onPlatformChange = (newType: string) => {
    if (newType === 'yipay') {
        // 切到易支付，保留易支付字段，清理支付宝字段
        const kept: Record<string, any> = {}
        if (channelConfig.value.url) kept.url = channelConfig.value.url
        if (channelConfig.value.pid) kept.pid = channelConfig.value.pid
        if (channelConfig.value.key) kept.key = channelConfig.value.key
        channelConfig.value = kept
        channelForm.value.platform = 1
    } else if (newType === 'alipay_dmf') {
        // 切到当面付，保留支付宝字段，设置产品码
        const kept: Record<string, any> = {}
        if (channelConfig.value.app_id) kept.app_id = channelConfig.value.app_id
        if (channelConfig.value.public_key) kept.public_key = channelConfig.value.public_key
        if (channelConfig.value.private_key) kept.private_key = channelConfig.value.private_key
        kept.product_code = 'FACE_TO_FACE_PAYMENT'
        channelConfig.value = kept
        channelForm.value.platform = 2
        selectedTypes.value = []
    } else if (newType === 'alipay_web') {
        // 切到网站支付，保留支付宝字段，设置产品码
        const kept: Record<string, any> = {}
        if (channelConfig.value.app_id) kept.app_id = channelConfig.value.app_id
        if (channelConfig.value.public_key) kept.public_key = channelConfig.value.public_key
        if (channelConfig.value.private_key) kept.private_key = channelConfig.value.private_key
        kept.product_code = 'FAST_INSTANT_TRADE_PAY'
        channelConfig.value = kept
        channelForm.value.platform = 2
        selectedTypes.value = []
    }
}

// 配置摘要
const configSummary = (configStr: any) => {
    if (!configStr) return '-'
    try {
        const obj = typeof configStr === 'string' ? JSON.parse(configStr) : configStr
        const keys = Object.keys(obj)
        if (keys.length === 0) return '-'
        return keys.map(k => `${k}=${obj[k]}`).join(', ')
    } catch {
        return String(configStr)
    }
}

// 将 config 字符串解析为编辑用的对象
const parseConfig = (configStr: any): Record<string, any> => {
    if (!configStr) return {}
    try {
        return typeof configStr === 'string' ? JSON.parse(configStr) : { ...configStr }
    } catch {
        return {}
    }
}

// 将配置对象序列化为字符串
const stringifyConfig = (obj: Record<string, any>): string => {
    return JSON.stringify(obj)
}

const getChannelList = async () => {
    loading.value = true
    try {
        const res = await axios.post(baseURL + '/api/shop/tenant/payChannels', {}, { headers })
        if (res.data.code === 0) {
            const data = res.data.data
            // 新结构：{ list, order_pay_mode }
            if (data && data.list) {
                channelList.value = data.list
                orderPayMode.value = data.order_pay_mode || ''
            } else if (Array.isArray(data)) {
                // 兼容旧结构
                channelList.value = data
                orderPayMode.value = res.data.order_pay_mode || ''
            } else {
                channelList.value = []
            }
        } else {
            message.error(res.data.msg || '获取支付通道失败')
        }
    } catch (e: any) {
        message.error('获取支付通道失败：' + e.message)
    } finally {
        loading.value = false
    }
}

const openAddDialog = () => {
    isEdit.value = false
    channelForm.value = { id: 0, name: '', platform: 1, type: [], config: '', state: 1 }
    editType.value = 'yipay'
    channelConfig.value = {}
    selectedTypes.value = []
    dialogVisible.value = true
}

const openEditDialog = (row: any) => {
    isEdit.value = true
    channelForm.value = { ...row }
    channelConfig.value = parseConfig(row.config)
    editType.value = detectEditType(row)
    // 易支付时初始化多选支付类型
    if (editType.value === 'yipay') {
        selectedTypes.value = parseType(row.type)
    } else {
        selectedTypes.value = []
    }
    dialogVisible.value = true
}

const submitChannel = async () => {
    // 同步platform和type
    if (editType.value === 'yipay') {
        channelForm.value.platform = 1
        if (selectedTypes.value.length === 0) {
            message.warning('请选择支付类型')
            return
        }
        channelForm.value.type = selectedTypes.value
    } else if (editType.value === 'alipay_dmf') {
        channelForm.value.platform = 2
        channelForm.value.type = ['alipay']
        channelConfig.value.product_code = 'FACE_TO_FACE_PAYMENT'
    } else if (editType.value === 'alipay_web') {
        channelForm.value.platform = 2
        channelForm.value.type = ['alipay']
        channelConfig.value.product_code = 'FAST_INSTANT_TRADE_PAY'
    }
    // 校验配置必填项
    const configValues = Object.values(channelConfig.value).filter(v => v !== undefined && v !== '')
    if (configValues.length === 0) {
        message.warning('请填写通道配置信息')
        return
    }
    submitting.value = true
    try {
        const url = isEdit.value ? '/api/shop/tenant/payChannel/update' : '/api/shop/tenant/payChannel/add'
        const payload = {
            ...channelForm.value,
            type: JSON.stringify(channelForm.value.type),
            config: stringifyConfig(channelConfig.value),
        }
        const res = await axios.post(baseURL + url, payload, { headers })
        if (res.data.code === 0) {
            message.success(isEdit.value ? '修改成功' : '添加成功')
            dialogVisible.value = false
            getChannelList()
        } else {
            message.error(res.data.msg || '操作失败')
        }
    } catch (e: any) {
        message.error('操作失败：' + e.message)
    } finally {
        submitting.value = false
    }
}

const deleteChannel = (row: any) => {
    Modal.confirm({
        title: '提示',
        content: `确认删除支付通道「${row.name || formatPlatform(row.platform)}」吗？`,
        okText: '确认删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
            try {
                const res = await axios.post(baseURL + '/api/shop/tenant/payChannel/delete', { id: row.id }, { headers })
                if (res.data.code === 0) {
                    message.success('删除成功')
                    getChannelList()
                } else {
                    message.error(res.data.msg || '删除失败')
                }
            } catch (e: any) {
                message.error('删除失败：' + e.message)
            }
        },
    })
}

onMounted(() => {
    getChannelList()
})
</script>

<style scoped lang="scss">
.pay-channel-content-div {
    height: 100vh;
    background-color: #f2f3f5 !important;
    overflow-y: auto;
}

.pay-channel-card {
    background-color: white;
    padding: 2rem;
    margin: 1.5rem;
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.pay-channel-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #2563eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.pay-channel-table {
    width: 100%;
}

.pay-mode-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.9rem 1rem;
    border-radius: 0.7rem;
    margin-bottom: 1rem;

    .notice-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 0.5rem;
        flex-shrink: 0;
        font-size: 0.9rem;
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        line-height: 1.6;
        color: #475569;

        strong {
            font-weight: 600;
        }
    }

    &.warning {
        background: #fffbeb;
        border: 1px solid #fde68a;

        .notice-icon {
            background: #fef3c7;
            color: #d97706;
        }

        p strong {
            color: #b45309;
        }
    }

    &.info {
        background: #eff6ff;
        border: 1px solid #bfdbfe;

        .notice-icon {
            background: #dbeafe;
            color: #2563eb;
        }

        p strong {
            color: #1e40af;
        }
    }
}

@media screen and (max-width: 768px) {
    .pay-channel-card {
        margin: 1rem;
        padding: 1.2rem;
    }
}
</style>
